import React, { useEffect, useState } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import styled from "./index.module.css";
import FiltersByEvent from "../../components/Filters/filtersbyEvent";
import EventApi from "../../services/EventApi";
import IApiEventsResponse from "../../interfaces/IApiEventsResponse";
import { useTranslation } from 'react-i18next';
import { formatDateTime } from "../../utilities/date";

const Report: React.FC = () => {
  const [events, setEvents] = useState<IApiEventsResponse[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<IApiEventsResponse[]>([]);
  const [initialData, setInitialData] = useState<IApiEventsResponse[]>([]);
  
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedOption, setSelectedOption] = useState<boolean | null>(null);
  const [inputValue, setInputValue] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { t: translateGeneral } = useTranslation("general");

  const fetchEvents = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await EventApi.GetAllEvents();
      const fetchedEvents = response.data || [];
      setEvents(fetchedEvents);
      setFilteredEvents(fetchedEvents);
      setInitialData(fetchedEvents);
    } catch (error) {
      console.error("Erro ao buscar eventos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const resetFilters = (): void => {
    setEvents(initialData);
    setFilteredEvents(initialData);
    setStartDate(null);
    setEndDate(null);
    setSelectedOption(null);
    setInputValue(null);
    filterEvents(); 
  };

  const filterEvents = (): void => {
    let filtered = events;

    if (startDate) {
      filtered = filtered.filter(event => {
        const eventDate = new Date(event.eventDate);
        return eventDate >= startDate; 
      });
    }

    if (endDate) {
      filtered = filtered.filter(event => {
        const eventDate = new Date(event.eventDate);
        return eventDate <= endDate; 
      });
    }

    if (selectedOption !== null) {
      filtered = filtered.filter((event: IApiEventsResponse): boolean => {
        return event.status === selectedOption;
      });
    }

    if (inputValue != null) {
      filtered = filtered.filter((event: IApiEventsResponse): boolean => {
          return String(event.accuracy).includes(inputValue);
      });
  }
  

    setFilteredEvents(filtered);
  };

  // formatDate removed as it is now imported or managed centrally if needed, 
  // but and we used formatDateTime in the column body instead.

  const getRowsPerPageOptions = (): number[] => {
    const options = [5, 10, 25, 50, 100];
    const filteredLength = filteredEvents.length;

    if (filteredLength > 100) {
        options.push(filteredLength);
    }
    const uniqueOptions = [...new Set([...options, filteredLength])].sort((a, b) => a - b);
    
    return uniqueOptions.filter(option => option <= filteredLength);
  };


  const footer = `${translateGeneral("totalRegisters")}: ${filteredEvents ? filteredEvents.length : 0}`;
  
  return (
    <div>
      <FiltersByEvent
       startDate={startDate} 
       endDate={endDate} 
       selectedOption={selectedOption}
       inputValue={inputValue}
       setStartDate={setStartDate}
       setEndDate={setEndDate}
       setSelectedOption={setSelectedOption}
       setInputValue={setInputValue}
       applyFilters={filterEvents}
       resetFilters={resetFilters}
      />

      {isLoading ? (
         <div>{translateGeneral("loading")}</div>
      ) : (
        <DataTable 
          value={filteredEvents} 
          paginator rows={5} 
          rowsPerPageOptions={getRowsPerPageOptions()} 
          dataKey="id" 
          className={styled.tablehistory}
          loading={isLoading}
          footer ={footer}
        >
          <Column 
            field="eventDate" 
            header="Data do Evento" 
            body={(rowData: IApiEventsResponse) => formatDateTime(rowData.eventDate)}
            sortable
            headerStyle={{ textAlign: 'center' }}
            bodyStyle={{ textAlign: 'center' }}
          />        
          <Column 
            field="status" 
            header="Resultado" 
            body={(rowData: IApiEventsResponse) => (rowData.status ? "OK" : "NO OK")}
            sortable
            headerStyle={{ textAlign: 'center' }} 
            bodyStyle={{ textAlign: 'center' }} 
          />
          <Column 
            field="accuracy" 
            header="Acurácia" 
            sortable
            body={(rowData: IApiEventsResponse) => `${rowData.accuracy}%`}
            headerStyle={{ textAlign: 'center' }} 
            bodyStyle={{ textAlign: 'center' }} 
          />
        </DataTable>
      )}
    </div>
  );
};

export default Report;
